import React, { useState } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import Swal from "sweetalert2";
import AdminHeader from './AdminHeader';
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
// Import icons from react-icons
import {
  FaThLarge, // Dashboard
  FaAdversal, // Advertisement
  FaUsers, // Users
  FaRegNewspaper, // Reporter (changed)
  FaHistory,
  FaUserTie, // Marketer
  FaVideo, // Conference
  FaAd, // Free Ad
  FaMoneyBillWave, // Payments
  FaIdCard, // ID Cards
  FaUserShield, // Admin
  FaGavel, // Darbar
  FaBullhorn, // Voices
  FaBuilding, // Organizations
  FaTicketAlt, // Coupon
  FaUserCog, // Profile
  FaCog, // Settings
  FaSignOutAlt, // Logout
  FaCheckCircle, // Verified Tick
  FaTimesCircle, // Rejected Cross
  FaRegCreditCard, // Payment History
  FaWallet, // Withdrawal History
  FaMicrophone, // Press Conference
  FaDollarSign, // Paid Conference
  FaCalendarAlt, // Calendar
  FaChartLine // Ads Tracking
} from "react-icons/fa";

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false); // NEW
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to log out?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, log out",
    }).then((result) => {
      if (result.isConfirmed) {
        localStorage.removeItem("token");
        navigate("/admin/login");
        Swal.fire("Logged Out", "You have been logged out.", "success");
      }
    });
  };

  const onNavigate = (path) => {
    navigate(`/admin/${path}`);
  };

  const menuItems = [
    {
      title: "🏠 Dashboard",
      titleColor: "text-blue-600",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200",
      items: [
        {
          path: "dashboard",
          icon: FaThLarge,
          label: "Overview Dashboard",
          description: "Main dashboard with analytics",
          gradient: "from-blue-500 to-blue-600",
          iconColor: "text-blue-500"
        }
      ]
    },
    {
      title: "📢 Advertisement Management",
      titleColor: "text-orange-600",
      bgColor: "bg-orange-50",
      borderColor: "border-orange-200",
      items: [
        {
          path: "freeadsdashboard",
          icon: FaAd,
          label: "Reward ads",
          description: "Manage free ad campaigns",
          gradient: "from-orange-500 to-orange-600",
          iconColor: "text-orange-500"
        },
        {
          path: "getalladvertisement",
          icon: FaAd,
          label: "Paid Advertisements",
          description: "Manage paid ad campaigns",
          gradient: "from-orange-500 to-orange-600",
          iconColor: "text-orange-500"
        },
        {
          path: "priceset",
          icon: FaCog,
          label: "Pricing Settings",
          description: "Configure ad pricing",
          gradient: "from-purple-500 to-purple-600",
          iconColor: "text-purple-500"
        }
      ]
    },
    {
      title: "📰 Reporter Management",
      titleColor: "text-yellow-600",
      bgColor: "bg-yellow-50",
      borderColor: "border-yellow-200",
      items: [
        {
          path: "user-ads-tracking",
          icon: FaChartLine,
          label: "Ads Tracking",
          description: "Track user ad performance",
          gradient: "from-indigo-500 to-indigo-600",
          iconColor: "text-indigo-500"
        },
        {
          path: "reportercompletedads",
          icon: FaRegNewspaper,
          label: "Completed Ads",
          description: "Reporter completed advertisements",
          gradient: "from-green-500 to-green-600",
          iconColor: "text-green-500"
        },
        {
          path: "reporterrejectedads",
          icon: FaRegNewspaper,
          label: "Rejected Ads",
          description: "Reporter rejected advertisements",
          gradient: "from-red-500 to-red-600",
          iconColor: "text-red-500"
        }
      ]
    },
    {
      title: "⚖️ Advocate Management",
      titleColor: "text-blue-700",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200",
      items: [
        {
          path: "totaladvocates",
          icon: FaGavel,
          label: "All Advocates",
          description: "View all registered advocates",
          gradient: "from-blue-600 to-blue-700",
          iconColor: "text-blue-600"
        }
      ]
    },
    {
      title: "🆔 ID Card Management",
      titleColor: "text-indigo-600",
      bgColor: "bg-indigo-50",
      borderColor: "border-indigo-200",
      items: [
        {
          path: "idcardrequests",
          icon: FaIdCard,
          label: "New ID Requests",
          description: "Pending ID card applications",
          gradient: "from-indigo-500 to-indigo-600",
          iconColor: "text-indigo-500"
        },
        {
          path: "approvedcards",
          icon: FaCheckCircle,
          label: "Verified ID Cards",
          description: "Approved ID cards",
          gradient: "from-green-500 to-green-600",
          iconColor: "text-green-500"
        },
        {
          path: "rejectedcards",
          icon: FaTimesCircle,
          label: "Rejected ID Cards",
          description: "Rejected ID card applications",
          gradient: "from-red-500 to-red-600",
          iconColor: "text-red-500"
        }
      ]
    },
    {
      title: "🎤 Press Conference",
      titleColor: "text-green-600",
      bgColor: "bg-green-50",
      borderColor: "border-green-200",
      items: [
        {
          path: "press-conference/users",
          icon: FaUsers,
          label: "Press Organizers",
          description: "All press conference user accounts",
          gradient: "from-teal-500 to-green-600",
          iconColor: "text-teal-500"
        },
        {
          path: "press-conference/free-conference",
          icon: FaMicrophone,
          label: "Free Conferences",
          description: "Manage free press conferences",
          gradient: "from-green-500 to-green-600",
          iconColor: "text-green-500"
        },
        {
          path: "press-conference/paid-conference",
          icon: FaDollarSign,
          label: "Paid Conferences",
          description: "Manage paid press conferences",
          gradient: "from-yellow-500 to-yellow-600",
          iconColor: "text-yellow-500"
        }
      ]
    },
    {
      title: "📢 Voice Management",
      titleColor: "text-emerald-600",
      bgColor: "bg-emerald-50",
      borderColor: "border-emerald-200",
      items: [
        {
          path: "raisevoicestatus",
          icon: FaBullhorn,
          label: "Raise Your Voices",
          description: "Manage voice submissions",
          gradient: "from-emerald-500 to-emerald-600",
          iconColor: "text-emerald-500"
        },
        {
          path: "voicehistory",
          icon: FaHistory,
          label: "Voice History",
          description: "View voice submission history",
          gradient: "from-emerald-500 to-emerald-600",
          iconColor: "text-emerald-500"
        }
      ]
    },
    {
      title: "🎙️ Podcast Management",
      titleColor: "text-purple-600",
      bgColor: "bg-purple-50",
      borderColor: "border-purple-200",
      items: [
        {
          path: "podcast/pending",
          icon: FaMicrophone,
          label: "Studio Submissions",
          description: "Manage podcast studio registrations",
          gradient: "from-purple-500 to-purple-600",
          iconColor: "text-purple-500"
        },
        {
          path: "podcast/booking",
          icon: FaCalendarAlt,
          label: "Booking Management",
          description: "Manage podcast booking requests",
          gradient: "from-purple-500 to-purple-600",
          iconColor: "text-purple-500"
        }
      ]
    },
    {
      title: "💰 Financial Management",
      titleColor: "text-teal-600",
      bgColor: "bg-teal-50",
      borderColor: "border-teal-200",
      items: [
        {
          path: "payment/history",
          icon: FaRegCreditCard,
          label: "Payment History",
          description: "View all payment transactions",
          gradient: "from-teal-500 to-teal-600",
          iconColor: "text-teal-500"
        },
        {
          path: "withdrawal/history",
          icon: FaWallet,
          label: "Withdrawal History",
          description: "View withdrawal transactions",
          gradient: "from-amber-500 to-amber-600",
          iconColor: "text-amber-500"
        },
        {
          path: "withdrawal/requests",
          icon: FaMoneyBillWave,
          label: "Withdrawal Requests",
          description: "Manage pending withdrawals",
          gradient: "from-teal-500 to-teal-600",
          iconColor: "text-teal-500"
        },
        {
          path: "refunds/history",
          icon: FaRegCreditCard,
          label: "Refund History",
          description: "View refund transactions",
          gradient: "from-teal-500 to-teal-600",
          iconColor: "text-teal-500"
        }
      ]
    },
    {
      title: "🎫 Coupon Management",
      titleColor: "text-purple-600",
      bgColor: "bg-purple-50",
      borderColor: "border-purple-200",
      items: [
        {
          path: "couponmanage",
          icon: FaTicketAlt,
          label: "Coupon Management",
          description: "Create and manage coupons",
          gradient: "from-purple-500 to-purple-600",
          iconColor: "text-purple-500"
        },
        {
          path: "couponhistory",
          icon: FaTicketAlt,
          label: "Coupon History",
          description: "View coupon usage history",
          gradient: "from-purple-500 to-purple-600",
          iconColor: "text-purple-500"
        }
      ]
    },
    {
      title: "👥 Admin Management",
      titleColor: "text-gray-600",
      bgColor: "bg-gray-50",
      borderColor: "border-gray-200",
      items: [
        {
          path: "getalladmins",
          icon: FaUserShield,
          label: "All Administrators",
          description: "View all admin accounts",
          gradient: "from-gray-500 to-gray-600",
          iconColor: "text-gray-500"
        },
        {
          path: "create/subadmin",
          icon: FaUserShield,
          label: "Add New Admin",
          description: "Create new admin accounts",
          gradient: "from-blue-500 to-blue-600",
          iconColor: "text-blue-500"
        }
      ]
    },
    {
      title: "⚙️ System Settings",
      titleColor: "text-slate-600",
      bgColor: "bg-slate-50",
      borderColor: "border-slate-200",
      items: [
        {
          path: "settings",
          icon: FaCog,
          label: "Admin Password",
          description: "Change admin password",
          gradient: "from-slate-500 to-slate-600",
          iconColor: "text-slate-500"
        },
        {
          path: "profile",
          icon: FaUserCog,
          label: "Profile Settings",
          description: "Manage admin profile",
          gradient: "from-slate-500 to-slate-600",
          iconColor: "text-slate-500"
        }
      ]
    }
  ];

  return (
    <div className="flex h-screen bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden">
      <style jsx="true">{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f8fafc;
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, #cbd5e1, #94a3b8);
          border-radius: 4px;
          border: 2px solid #f8fafc;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(to bottom, #94a3b8, #64748b);
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:active {
          background: linear-gradient(to bottom, #64748b, #475569);
        }
      `}</style>
      {/* Sidebar for desktop & overlay for mobile */}
      {/* Overlay background for mobile */}
      <div
        className={`
          fixed inset-0 z-30 bg-black bg-opacity-40 transition-opacity lg:hidden
          ${sidebarOpen ? "block" : "hidden"}
        `}
        onClick={() => setSidebarOpen(false)}
        aria-hidden="true"
      />
      {/* Sidebar */}
      <div
        className={`
          fixed z-40 top-0 left-0 h-full w-64 bg-gradient-to-b from-white via-gray-50 to-gray-100 shadow-2xl rounded-r-2xl
          transform transition-all duration-300 ease-in-out
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
          lg:static lg:translate-x-0 lg:w-64 lg:flex-shrink-0
        `}
        style={{ maxWidth: "100vw" }}
      >
        {/* Sidebar Header */}
        <div className="relative">
          <div className="bg-gradient-to-br from-emerald-600 via-teal-700 to-cyan-800 h-14 md:h-16 flex items-center justify-between px-4 rounded-tr-2xl lg:rounded-tr-none shadow-xl">
            <div className="flex items-center space-x-2">
              <div className="w-7 h-7 md:w-8 md:h-8 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm border border-white/10">
                <FaUserShield className="w-4 h-4 text-white" />
              </div>
              <div>
                <h2 className="text-sm md:text-lg font-black text-white uppercase tracking-tight">
                  Iinsaf Admin
                </h2>
                <p className="text-emerald-100 text-[8px] md:text-xs font-black uppercase tracking-widest opacity-80">Security Shell</p>
              </div>
            </div>
            {/* Close button for mobile */}
            <button
              className="text-white/80 hover:text-white hover:bg-white/10 p-1.5 md:p-2 rounded-lg transition-colors lg:hidden"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close sidebar"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-400 via-teal-500 to-cyan-600 opacity-40"></div>
        </div>

        {/* Scrollable Sidebar Content */}
        <div className="overflow-y-auto h-[calc(100vh-3.5rem)] px-2 py-3 custom-scrollbar">
          {menuItems.map((section, idx) => (
            <div key={idx} className="mb-4 md:mb-6">
              {/* Section Header */}
              <div className="mb-2 group px-2">
                <div className="flex items-center justify-between mb-1">
                  <h2 className={`font-black text-[10px] ${section.titleColor} flex items-center transition-all duration-300 uppercase tracking-widest opacity-60`}>
                    <span className="mr-1.5">{section.title.split(' ')[0]}</span>
                    <span className="hidden md:inline">
                      {section.title.split(' ').slice(1).join(' ')}
                    </span>
                  </h2>
                  <div className="flex items-center">
                    <span className="text-[8px] bg-white px-1.5 py-0.5 rounded-full shadow-sm border border-gray-100 font-black text-gray-400">
                      {section.items.length}
                    </span>
                  </div>
                </div>
                <div className={`h-px w-full rounded-full ${section.bgColor.replace('bg-', 'bg-gradient-to-r from-').replace('-50', '-200')} opacity-20`}></div>
              </div>

              {/* Section Items */}
              <div className="space-y-0.5">
                {section.items.map((item, itemIdx) => {
                  const isActive = location.pathname === `/admin/${item.path}`;
                  return (
                    <div
                      key={itemIdx}
                      onClick={() => {
                        onNavigate(item.path);
                        setSidebarOpen(false); // close sidebar on mobile after click
                      }}
                      className={`
                        group relative flex items-center space-x-2 md:space-x-3 px-2 md:px-3 py-1.5 md:py-2.5 rounded-lg md:rounded-xl cursor-pointer
                        transition-all duration-200
                        ${isActive
                          ? `bg-gradient-to-br ${item.gradient} shadow-md md:shadow-lg text-white`
                          : 'hover:bg-white text-gray-600 hover:text-gray-900 border border-transparent hover:border-gray-100 hover:shadow-sm'
                        }
                      `}
                    >
                      {/* Icon Container */}
                      <div className={`
                        flex-shrink-0 w-7 h-7 md:w-8 md:h-8 rounded-lg flex items-center justify-center
                        transition-all duration-300
                        ${isActive
                          ? 'bg-white/20 text-white shadow-inner'
                          : `${item.iconColor.replace('text-', 'bg-').replace('-500', '-50')} ${item.iconColor} group-hover:bg-white`
                        }
                      `}>
                        <item.icon className="w-3.5 h-3.5 md:w-4 md:h-4" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className={`
                          font-black text-[11px] md:text-xs transition-all duration-200 uppercase tracking-tight
                          ${isActive
                            ? 'text-white'
                            : 'text-gray-800'
                          }
                        `}>
                          {item.label}
                        </div>
                        <div className={`
                          text-[9px] md:text-xs mt-0.5 transition-all duration-200 leading-tight hidden md:block opacity-80
                          ${isActive
                            ? 'text-white'
                            : 'text-gray-400'
                          }
                        `}>
                          {item.description}
                        </div>
                      </div>

                      {/* Active Indicator */}
                      {isActive && (
                        <div className="flex-shrink-0">
                          <div className="w-1 md:w-1.5 h-1 md:h-1.5 bg-white rounded-full shadow-sm animate-pulse"></div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Logout Button */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center space-x-2 px-4 py-3 rounded-xl
                bg-gradient-to-r from-red-500 via-red-600 to-red-700 text-white
                transition-all duration-300 transform hover:scale-105 hover:shadow-lg
                group border border-red-400/20"
            >
              <div className="w-6 h-6 bg-white/20 rounded-md flex items-center justify-center group-hover:bg-white/30 transition-colors">
                <FaSignOutAlt className="w-3 h-3 transition-transform duration-300 group-hover:rotate-12" />
              </div>
              <div className="text-left">
                <div className="font-semibold text-xs">Sign Out</div>
                <div className="text-xs text-red-100">Logout from admin</div>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Fixed Header */}
        <div className="flex items-center justify-between px-3 h-12 bg-white shadow-sm lg:hidden sticky top-0 z-20">
          {/* Hamburger menu for mobile */}
          <button
            className="text-emerald-700 focus:outline-none"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open sidebar"
          >
            <svg className="w-6 h-6 md:w-7 md:h-7" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h8m-8 6h16" />
            </svg>
          </button>
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-black uppercase tracking-tighter">Iinsaf Admin</span>
            <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></div>
          </div>
          <div className="w-6 h-6" /> {/* Spacer for symmetry */}
        </div>
        <div className="hidden lg:block">
          <AdminHeader />
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto bg-gradient-to-br from-gray-50 to-gray-100">
          <div className="p-2 sm:p-3 md:p-4 lg:p-6">
            <div className="bg-white rounded-xl md:rounded-2xl shadow-sm p-2 sm:p-3 md:p-6 min-h-[calc(100vh-6rem)]">
              <Outlet />

            </div>
          </div>
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default AdminLayout;
